import { create } from 'zustand';
import type { DashboardUploaderS3Cfg, UploadItem } from '@/lib/types/Upload';

interface UploadState {
  // Drawer
  drawerOpen: boolean;
  drawerMinimized: boolean;

  // S3 credentials (shared across all upload types)
  credentials: DashboardUploaderS3Cfg | null;
  credentialsCId: number | null;

  // IngestCfg selection
  selectedIngestcfgId: number | null;

  // Upload items (video uploads, ordered by addition time)
  items: UploadItem[];
}

interface UploadActions {
  // Drawer
  openDrawer: () => void;
  closeDrawer: () => void;
  minimizeDrawer: () => void;
  restoreDrawer: () => void;

  // Credentials
  setCredentials: (cId: number, cfg: DashboardUploaderS3Cfg) => void;
  clearCredentials: () => void;

  // IngestCfg
  setSelectedIngestcfgId: (id: number) => void;

  // Items
  addItem: (item: UploadItem) => void;
  updateItem: (id: string, updates: Partial<UploadItem>) => void;
  updateItemByUppyId: (uppyFileId: string, updates: Partial<UploadItem>) => void;
  removeItem: (id: string) => void;
  clearCompleted: () => void;

  // Reset (customer switch / logout)
  reset: () => void;
}

const initialState: UploadState = {
  drawerOpen: false,
  drawerMinimized: false,
  credentials: null,
  credentialsCId: null,
  selectedIngestcfgId: null,
  items: [],
};

export const useUploadStore = create<UploadState & UploadActions>()((set, get) => ({
  ...initialState,

  // Drawer
  openDrawer: () => set({ drawerOpen: true, drawerMinimized: false }),
  closeDrawer: () => set({ drawerOpen: false }),
  minimizeDrawer: () => set({ drawerOpen: false, drawerMinimized: true }),
  restoreDrawer: () => set({ drawerOpen: true, drawerMinimized: false }),

  // Credentials
  setCredentials: (cId, cfg) => set({ credentials: cfg, credentialsCId: cId }),
  clearCredentials: () => set({ credentials: null, credentialsCId: null }),

  // IngestCfg
  setSelectedIngestcfgId: (id) => set({ selectedIngestcfgId: id }),

  // Items
  addItem: (item) => set((state) => ({ items: [...state.items, item] })),

  updateItem: (id, updates) =>
    set((state) => ({
      items: state.items.map((item) =>
        item.id === id ? { ...item, ...updates } : item
      ),
    })),

  updateItemByUppyId: (uppyFileId, updates) =>
    set((state) => ({
      items: state.items.map((item) =>
        item.uppyFileId === uppyFileId ? { ...item, ...updates } : item
      ),
    })),

  removeItem: (id) =>
    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
    })),

  clearCompleted: () =>
    set((state) => ({
      items: state.items.filter(
        (item) => item.status !== 'ready' && item.status !== 'error' && item.status !== 'cancelled'
      ),
    })),

  // Reset — clears everything for customer switch / logout.
  // Credentials cleared BEFORE Uppy's cancelAll() triggers abort callbacks.
  // S3MultipartSigner.abortMultipartUpload() has a null-guard for this case.
  reset: () => set(initialState),
}));

/**
 * Selector: count of uploads the client is still responsible for (queued or
 * sending bytes). Once an item reaches 'processing', ingest/transcode continue
 * server-side with no client involvement — account switching and tab close
 * are safe and cancel nothing.
 */
export function getInFlightUploadCount(): number {
  const { items } = useUploadStore.getState();
  return items.filter(
    (i) => i.status === 'pending' || i.status === 'creating' || i.status === 'uploading'
  ).length;
}

/** Selector: count of uploads in server-side processing (post-upload). */
export function getProcessingCount(): number {
  const { items } = useUploadStore.getState();
  return items.filter((i) => i.status === 'processing').length;
}
